import { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode'

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// eslint-disable-next-line react/prop-types
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = (token) => {
    setLoading(true);
    fetch(`${import.meta.env.VITE_API_URL}auth/verify`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Token verification failed');
        }
      })
      .then(userData => {
        setUser(userData);
        setLoading(false);
      })
      .catch(error => {
        console.error('Authentication error:', error);
        setError('Authentication error');
        setLoading(false);
      });
  };

  const login = (token) => {
    setLoading(true);
    localStorage.setItem('token', token);
    verifyToken(token);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshToken = () => {
    const refresh_token = localStorage.getItem('refreshToken');
    if (refresh_token) {
      fetch(`${import.meta.env.VITE_API_URL}auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken: refresh_token })
      })
        .then(response => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('Failed to refresh token');
          }
        })
        .then(data => {
          localStorage.setItem('token', data.accessToken);
          verifyToken(data.accessToken);
        })
        .catch(error => {
          console.error('Token refresh error:', error);
          setError('Failed to refresh token');
          setLoading(false);
        });
    } else {
      logout();
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const { exp } = jwtDecode(token);
      const now = Date.now().valueOf() / 1000;
      if (exp && now >= exp - 60) {
        refreshToken();
      }
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
